import React from "react";
import {Card, Col, Row, Spin} from "antd";
import {Redirect} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useStore} from "hooks/useStore";
import {useFetch} from "hooks/useFetch";
import {APPLICATION_TYPES} from "constants/application";
import {
    formatDate,
    getApplicationTitle,
    getZoneLabel,
    getZoneManagementLabel
} from "../common/applicationUtils";
import {ApplicationElapsedTime} from "../common/ApplicationElapsedTime";
import {ApplicationInfoCard} from "./ApplicationInfoCard";
import {ApplicationStatusEditor} from "./ApplicationStatusEditor";
import {ApplicationHistoryTabs} from "./ApplicationHistoryTabs";
import {ApplicationTicketsSection} from "./ApplicationTicketsSection";

const joinNames = items => (items || []).map(item => item.name).join(", ") || "-";

export const ApplicationDetailPage = ({type, applicationId}) => {
    const {t} = useTranslation(["applications"]);
    const stores = useStore();
    const isCompany = type === APPLICATION_TYPES.company;
    const canView = isCompany || stores.meStore.checkRole("agencyApplicationView");

    const [loading, detail, , , refetch] = useFetch({
        fetchFn: stores.applicationStore.getApplication.bind(null, type, applicationId),
        defaultDataValue: {},
        dependencies: [type, applicationId],
        condition: canView
    });

    if (!canView) {
        return <Redirect to="/applications" />;
    }

    const application = detail?.data?.application;
    const canEdit = !!detail?.data?.permissions?.canEdit;
    const text = (scope, key) => t(`detail.${scope}.${key}`, {ns: "applications"});

    const businessItems = application && [
        {label: text("company", "companyName"), value: application.companyName},
        {label: text("company", "city"), value: application.city?.name},
        {label: text("company", "district"), value: application.district?.name},
        {label: text("company", "zone"), value: getZoneLabel(application)},
        {label: text("company", "zoneManagement"), value: getZoneManagementLabel(application)},
        {label: text("company", "neighborhood"), value: application.neighborhood?.name},
        {label: text("company", "address"), value: application.address},
        {
            label: text("company", "businessHours"),
            value:
                application.businessHoursStart && application.businessHoursEnd
                    ? `${application.businessHoursStart} - ${application.businessHoursEnd}`
                    : "-"
        },
        {
            label: text("company", "dailyPackage"),
            value: application.estimatedDailyPackageLabel,
            span: 2
        },
        {label: text("company", "note"), value: application.note, span: 2}
    ];

    const sourceItems = application && [
        {label: text("source", "pageLink"), value: application.meta?.pageLink},
        {label: text("source", "utmSource"), value: application.meta?.utmSource},
        {label: text("source", "utmMedium"), value: application.meta?.utmMedium},
        {label: text("source", "utmCampaign"), value: application.meta?.utmCampaign},
        {label: text("source", "utmContent"), value: application.meta?.utmContent},
        {label: text("source", "utmTerm"), value: application.meta?.utmTerm}
    ];

    const agencyItems = application && [
        {label: text("agency", "city"), value: application.city?.name},
        {label: text("agency", "districts"), value: joinNames(application.districts)},
        {label: text("agency", "zone"), value: getZoneLabel(application)},
        {
            label: text("agency", "customerPortfolio"),
            value: text("agency", application.hasCustomerPortfolio ? "yes" : "no")
        },
        {
            label: text("agency", "courierPortfolio"),
            value: text("agency", application.hasCourierPortfolio ? "yes" : "no"),
            span: 2
        },
        {label: text("agency", "message"), value: application.message, span: 2}
    ];

    return (
        <div style={{padding: "24px"}}>
            <div style={{display: "flex", alignItems: "center", gap: 12, marginBottom: 16}}>
                <h2 style={{margin: 0}}>
                    {application ? getApplicationTitle(application, type) : ""}
                </h2>
            </div>
            <Spin spinning={loading}>
                {application && (
                    <>
                        <Row gutter={[16, 16]} style={{marginBottom: 16}}>
                            <Col xs={24} lg={12}>
                                <ApplicationInfoCard
                                    title={text("contact", "title")}
                                    items={[
                                        {
                                            label: text("contact", "fullName"),
                                            value: application.fullName
                                        },
                                        {label: text("contact", "phone"), value: application.phone},
                                        {label: text("contact", "email"), value: application.email}
                                    ]}
                                />
                            </Col>
                            <Col xs={24} lg={12}>
                                <ApplicationInfoCard
                                    title={text("process", "title")}
                                    items={[
                                        {
                                            label: text("process", "status"),
                                            value: (
                                                <ApplicationStatusEditor
                                                    type={type}
                                                    application={application}
                                                    canEdit={canEdit}
                                                    onChanged={refetch}
                                                />
                                            )
                                        },
                                        {
                                            label: text("process", "createdAt"),
                                            value: formatDate(application.createdAt)
                                        },
                                        {
                                            label: text("process", "elapsed"),
                                            value: (
                                                <ApplicationElapsedTime application={application} />
                                            )
                                        }
                                    ]}
                                />
                            </Col>
                        </Row>
                        <div style={{marginBottom: 16}}>
                            <ApplicationInfoCard
                                title={text(isCompany ? "company" : "agency", "title")}
                                items={isCompany ? businessItems : agencyItems}
                                column={2}
                                balanced
                            />
                        </div>
                        {isCompany && (
                            <div style={{marginBottom: 16}}>
                                <ApplicationInfoCard
                                    title={text("source", "title")}
                                    items={sourceItems}
                                    column={2}
                                />
                            </div>
                        )}
                        <ApplicationTicketsSection
                            type={type}
                            application={application}
                            canEdit={canEdit}
                        />
                        <Card size="small">
                            <ApplicationHistoryTabs
                                type={type}
                                application={application}
                                notes={detail.data.notes || []}
                                history={detail.data.history || []}
                                previousApplications={detail.data.previousApplications || []}
                                canEdit={canEdit}
                                onChanged={refetch}
                            />
                        </Card>
                    </>
                )}
            </Spin>
        </div>
    );
};
